import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { StreamWithChatRate, GameFilter } from '../types';
import { TwitchUser, createShareCode } from '../services/twitchApi';
import { Channel, ChannelId, CHANNELS, MAX_CHANNEL_STREAMS, getChannel, isCrewChannel } from '../constants/channels';
import { MAX_CUSTOM_STREAMS } from './Settings';
import { GamePicker } from './GamePicker';
import { SourceSwitch } from './SourceSwitch';
import { ChannelForm, ChannelFormValues } from './ChannelForm';
import { StreamRow, RosterOfflineRow } from './StreamRow';
import { sharePath } from '../utils/appRoutes';
import { getHeatRatio } from '../utils/heat';
import { filterNpxlStreams } from '../utils/npxlStreams';
import { IconSearch, IconGlitch, IconLogout, IconChevronLeft, IconShare } from './icons';
import './StreamList.css';

/**
 * What a channel admin can do to the dial. Every call lands on the server
 * and resolves once the catalogue has been re-racked from its answer;
 * a rejection carries the server's message.
 */
export interface ChannelAdminActions {
  saveRoster: (id: ChannelId, streams: string[]) => Promise<void>;
  /** Resolves to the new channel's id */
  createChannel: (values: ChannelFormValues) => Promise<ChannelId>;
  updateChannel: (id: ChannelId, values: ChannelFormValues) => Promise<void>;
  deleteChannel: (id: ChannelId) => Promise<void>;
}

interface StreamListProps {
  streams: StreamWithChatRate[];
  followedStreams?: StreamWithChatRate[];
  customStreams?: string[];
  /** Channels from the custom list / active crew resolved via Helix lookup */
  listStreams?: StreamWithChatRate[];
  /** Profiles for offline list entries (login -> user) */
  listUsers?: Record<string, TwitchUser>;
  /** The active channel — what the list shows and what auto-switch draws from */
  streamSource: ChannelId;
  onStreamSourceChange: (source: ChannelId) => void;
  /** The live catalogue — the built-in constants until the server's arrives */
  channels?: Channel[];
  /** Set for channel admins: roster edits and new channels land on the server, for everyone */
  channelAdmin?: ChannelAdminActions | null;
  /** Active crew's roster (server custom-lists.json first, constants as fallback) */
  recRoster?: string[];
  onCustomStreamsChange?: (streams: string[]) => void;
  /** Set while viewing someone else's list — "@handle" or "Shared list" */
  sharedLabel?: string | null;
  onAdoptSharedList?: () => void;
  onLeaveSharedList?: () => void;
  gameFilter?: GameFilter | null;
  onGameFilterChange?: (game: GameFilter | null) => void;
  selectedStream: StreamWithChatRate | null;
  onStreamSelect: (stream: StreamWithChatRate) => void;
  onToggleSidebar?: () => void;
  user?: TwitchUser | null;
  liveCount?: number | null;
  onLogin?: () => void;
  onLogout?: () => void;
  onShowGuide?: () => void;
}

type SortOption = 'chat' | 'viewers' | 'heat';
type ViewMode = ChannelId;

interface OfflineEntry {
  login: string;
  user?: TwitchUser;
}

const cleanLogin = (raw: string): string =>
  raw.trim().toLowerCase()
    .replace(/^(?:https?:\/\/)?(?:www\.)?twitch\.tv\//, '')
    .replace(/[^a-z0-9_]/g, '');

export const StreamList: React.FC<StreamListProps> = ({
  streams,
  followedStreams = [],
  customStreams = [],
  listStreams = [],
  listUsers = {},
  streamSource,
  onStreamSourceChange,
  channels = CHANNELS,
  channelAdmin,
  recRoster,
  onCustomStreamsChange,
  sharedLabel,
  onAdoptSharedList,
  onLeaveSharedList,
  gameFilter,
  onGameFilterChange,
  selectedStream,
  onStreamSelect,
  onToggleSidebar,
  user,
  liveCount,
  onLogin,
  onLogout,
  onShowGuide,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('chat');
  const [addInput, setAddInput] = useState('');
  const [shareState, setShareState] = useState<
    { status: 'idle' } | { status: 'busy' } | { status: 'copied' | 'ready'; url: string } | { status: 'error' }
  >({ status: 'idle' });

  // The list shows the active channel — the same pool auto-switch draws
  // from. A shared link (/@handle, /s/CODE) always shows as Custom without
  // touching the saved source; Following needs a login to mean anything.
  const viewMode: ViewMode = sharedLabel
    ? 'custom'
    : streamSource === 'followed' && !user ? 'top' : streamSource;
  const channel = getChannel(viewMode, channels);
  const isCrew = isCrewChannel(viewMode, channels);

  // A share link belongs to one exact list — invalidate the copied state as
  // soon as the list changes
  useEffect(() => {
    setShareState({ status: 'idle' });
  }, [customStreams]);

  const shareCurrentList = async () => {
    if (customStreams.length === 0 || shareState.status === 'busy') return;
    setShareState({ status: 'busy' });
    const code = await createShareCode(customStreams);
    if (!code) {
      setShareState({ status: 'error' });
      return;
    }
    const url = `${window.location.origin}${sharePath(code)}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareState({ status: 'copied', url });
    } catch {
      // Clipboard can be blocked — surface the link for manual copying
      setShareState({ status: 'ready', url });
    }
  };

  // One entry per channel across every pool. Top-100 entries win, but any
  // entry with live data replaces a bare placeholder.
  const mergedByLogin = useMemo(() => {
    const map = new Map<string, StreamWithChatRate>();
    [...streams, ...followedStreams, ...listStreams].forEach(s => {
      const key = s.user_login.toLowerCase();
      const existing = map.get(key);
      if (!existing || (!existing.thumbnail_url && s.thumbnail_url)) {
        map.set(key, s);
      }
    });
    return map;
  }, [streams, followedStreams, listStreams]);

  // Crews and custom are rosters: every listed channel appears, live or offline
  const roster = useMemo(() => {
    if (!isCrew && viewMode !== 'custom') return null;
    const logins = isCrew
      ? (recRoster ?? channel.fallbackStreamers ?? [])
      : customStreams;
    if (viewMode === 'npxl') {
      // Use the latest roster lookup, not stale categories from the Top 100.
      return {
        live: filterNpxlStreams(listStreams, logins),
        offline: logins.map(name => ({ login: name.toLowerCase(), user: listUsers[name.toLowerCase()] })),
      };
    }
    const seen = new Set<string>();
    const live: StreamWithChatRate[] = [];
    const offline: OfflineEntry[] = [];
    logins.forEach(name => {
      const key = name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const stream = mergedByLogin.get(key);
      if (stream && stream.thumbnail_url) {
        live.push(stream);
      } else {
        offline.push({ login: key, user: listUsers[key] });
      }
    });
    return { live, offline };
  }, [viewMode, isCrew, channel.fallbackStreamers, recRoster, customStreams, mergedByLogin, listUsers, listStreams]);

  const activeStreams = useMemo(() => {
    if (roster) return roster.live;
    if (viewMode === 'followed') {
      if (!user) return [];
      const seen = new Set<string>();
      return followedStreams.filter(s => {
        const key = s.user_login.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    return streams;
  }, [roster, viewMode, user, streams, followedStreams]);

  const filteredAndSortedStreams = useMemo(() => {
    let result = [...activeStreams];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(stream =>
        stream.user_name.toLowerCase().includes(search) ||
        stream.title.toLowerCase().includes(search) ||
        stream.game_name.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'viewers':
          return b.viewer_count - a.viewer_count;
        case 'heat':
          return getHeatRatio(b) - getHeatRatio(a);
        case 'chat':
        default:
          return b.chatRate - a.chatRate;
      }
    });

    return result;
  }, [activeStreams, searchTerm, sortBy]);

  const filteredOffline = useMemo(() => {
    if (!roster) return [];
    const search = searchTerm.trim().toLowerCase();
    if (!search) return roster.offline;
    return roster.offline.filter(entry =>
      entry.login.includes(search) ||
      entry.user?.display_name.toLowerCase().includes(search)
    );
  }, [roster, searchTerm]);

  // ————— list editing: your custom list, or (channel admins) a channel's roster —————

  const canEditCustom = viewMode === 'custom' && !!onCustomStreamsChange;
  const canEditCrew = isCrew && !!channelAdmin;
  const canEdit = canEditCustom || canEditCrew;
  // What the editor works on: a channel's roster as shown (server first,
  // constants as fallback), or the visitor's own list
  const editList = useMemo(
    () => (canEditCrew ? (recRoster ?? channel.fallbackStreamers ?? []) : customStreams),
    [canEditCrew, recRoster, channel.fallbackStreamers, customStreams]
  );
  const editMax = canEditCrew ? MAX_CHANNEL_STREAMS : MAX_CUSTOM_STREAMS;
  const customSet = useMemo(
    () => new Set(editList.map(s => s.toLowerCase())),
    [editList]
  );

  // Roster edits save to the server straight away; the status line says how it went
  const [rosterStatus, setRosterStatus] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  useEffect(() => {
    if (rosterStatus.kind !== 'saved') return;
    const timer = setTimeout(() => setRosterStatus({ kind: 'idle' }), 2500);
    return () => clearTimeout(timer);
  }, [rosterStatus]);
  useEffect(() => {
    setRosterStatus({ kind: 'idle' });
  }, [viewMode]);

  const commitList = useCallback((next: string[]) => {
    if (canEditCrew && channelAdmin) {
      setRosterStatus({ kind: 'saving' });
      channelAdmin.saveRoster(viewMode, next)
        .then(() => setRosterStatus({ kind: 'saved' }))
        .catch((err: any) => setRosterStatus({ kind: 'error', message: err?.message || 'Save failed' }));
    } else if (onCustomStreamsChange) {
      onCustomStreamsChange(next);
    }
  }, [canEditCrew, channelAdmin, viewMode, onCustomStreamsChange]);

  const addCustom = (raw: string) => {
    if (!canEdit) return;
    const login = cleanLogin(raw);
    if (!login || customSet.has(login) || editList.length >= editMax) return;
    commitList([...editList, login]);
    setAddInput('');
  };

  const removeCustom = useCallback((login: string) => {
    const key = login.toLowerCase();
    commitList(editList.filter(s => s.toLowerCase() !== key));
  }, [editList, commitList]);

  const importFollows = () => {
    if (!onCustomStreamsChange) return;
    const merged = [...customStreams];
    const have = new Set(customStreams.map(s => s.toLowerCase()));
    followedStreams.forEach(s => {
      const login = s.user_login.toLowerCase();
      if (!have.has(login) && merged.length < MAX_CUSTOM_STREAMS) {
        have.add(login);
        merged.push(login);
      }
    });
    if (merged.length > customStreams.length) onCustomStreamsChange(merged);
  };

  const watchingLogin = selectedStream?.user_login.toLowerCase() || '';
  const canAddWatching = canEdit && !!watchingLogin && !customSet.has(watchingLogin)
    && editList.length < editMax;
  const canImportFollows = canEditCustom && !!user && followedStreams.length > 0
    && customStreams.length < MAX_CUSTOM_STREAMS;

  // ————— the channel form: an admin racks a new channel, or edits one they made —————

  const [channelForm, setChannelForm] = useState<'create' | 'edit' | null>(null);
  const [channelFormBusy, setChannelFormBusy] = useState(false);
  const [channelFormError, setChannelFormError] = useState<string | null>(null);
  useEffect(() => {
    // Tuning away closes an edit in progress
    setChannelForm(prev => (prev === 'edit' ? null : prev));
    setChannelFormError(null);
  }, [viewMode]);

  const openChannelForm = (mode: 'create' | 'edit') => {
    setChannelForm(mode);
    setChannelFormError(null);
  };

  const closeChannelForm = () => {
    setChannelForm(null);
    setChannelFormError(null);
  };

  const submitChannelForm = async (values: ChannelFormValues) => {
    if (!channelAdmin || !channelForm) return;
    setChannelFormBusy(true);
    setChannelFormError(null);
    try {
      if (channelForm === 'create') {
        const id = await channelAdmin.createChannel(values);
        onStreamSourceChange(id);
      } else {
        await channelAdmin.updateChannel(viewMode, values);
      }
      setChannelForm(null);
    } catch (err: any) {
      setChannelFormError(err?.message || 'Something went wrong');
    } finally {
      setChannelFormBusy(false);
    }
  };

  const deleteChannel = async () => {
    if (!channelAdmin || channel.builtin !== false) return;
    if (!window.confirm(`Delete the ${channel.label} channel for everyone? Its roster and chat room go with it.`)) return;
    setChannelFormBusy(true);
    setChannelFormError(null);
    try {
      await channelAdmin.deleteChannel(viewMode);
      setChannelForm(null);
    } catch (err: any) {
      setChannelFormError(err?.message || 'Something went wrong');
    } finally {
      setChannelFormBusy(false);
    }
  };

  const listTitle =
    viewMode === 'custom' ? (sharedLabel || 'Custom')
    : viewMode === 'top' && gameFilter ? gameFilter.name
    : channel.label;

  // Velocity meters are scaled against the loudest chat in the visible list
  const listPeakRate = useMemo(
    () => filteredAndSortedStreams.reduce((max, s) => Math.max(max, s.chatRate10s || 0), 0),
    [filteredAndSortedStreams]
  );

  return (
    <div className="stream-list">
      <div className="stream-list-header">
        {/* Channel dial — what you see is what auto-switch plays inside */}
        <SourceSwitch
          source={viewMode}
          onChange={onStreamSourceChange}
          isLoggedIn={!!user}
          sharedLabel={sharedLabel}
          channels={channels}
          onNewChannel={channelAdmin ? () => openChannelForm('create') : undefined}
        />

        {/* Channel admin: rack a new channel, or rename / re-rack / delete one you made */}
        {channelForm && channelAdmin && (
          <ChannelForm
            key={`${channelForm}:${viewMode}`}
            mode={channelForm}
            initial={channelForm === 'edit' ? { label: channel.label, tag: channel.tag === 'just' ? 'just' : '' } : undefined}
            busy={channelFormBusy}
            error={channelFormError}
            onSubmit={submitChannelForm}
            onCancel={closeChannelForm}
            onDelete={channelForm === 'edit' && channel.builtin === false ? deleteChannel : undefined}
          />
        )}

        {/* Login Section */}
        <div className="twitch-login-section">
          {user ? (
            <div className="user-info">
              <img src={user.profile_image_url} alt={user.display_name} className="user-avatar" />
              <span className="user-name">{user.display_name}</span>
              <button className="help-btn small" onClick={onShowGuide} title="What is this?">?</button>
              <button className="logout-btn" onClick={onLogout} title="Logout">
                <IconLogout size={15} />
              </button>
            </div>
          ) : (
            <div className="login-row">
              <button className="twitch-login-btn" onClick={onLogin}>
                <IconGlitch size={18} />
                Connect Twitch
              </button>
              <button className="help-btn" onClick={onShowGuide} title="What is this?">?</button>
            </div>
          )}
        </div>

        {/* Category lock — rank and auto-switch inside one game's top 100 */}
        {viewMode === 'top' && onGameFilterChange && (
          <GamePicker gameFilter={gameFilter || null} onChange={onGameFilterChange} />
        )}

        {/* Viewing someone else's list — adopt it or step back to your own */}
        {viewMode === 'custom' && sharedLabel && (
          <div className="shared-banner">
            <span className="shared-banner-text">
              Viewing <strong>{sharedLabel}</strong> · {customStreams.length} channels
            </span>
            <div className="shared-banner-actions">
              {onAdoptSharedList && (
                <button className="quick-chip" onClick={onAdoptSharedList} title="Copy these channels into your own custom list">
                  Save a copy
                </button>
              )}
              {onLeaveSharedList && (
                <button className="quick-chip" onClick={onLeaveSharedList} title="Back to your own list">
                  Leave
                </button>
              )}
            </div>
          </div>
        )}

        {/* List editor — your custom list, or a channel's roster for a channel admin */}
        {canEdit && (
          <div className="list-editor">
            {canEditCrew && (
              <div className={`channel-admin-bar ${rosterStatus.kind === 'error' ? 'error' : ''}`}>
                <span className="channel-admin-text">
                  {rosterStatus.kind === 'saving' ? 'Saving…'
                    : rosterStatus.kind === 'saved' ? 'Saved — live for everyone'
                    : rosterStatus.kind === 'error' ? rosterStatus.message
                    : `Editing ${channel.label} · ${editList.length} streamers · changes go live for everyone`}
                </span>
                {channel.builtin === false && (
                  <button
                    className="quick-chip"
                    onClick={() => openChannelForm('edit')}
                    title="Rename this channel, move it to another row, or delete it"
                  >
                    Edit channel
                  </button>
                )}
              </div>
            )}
            <div className="settings-input-row list-editor-row">
              <input
                type="text"
                value={addInput}
                onChange={(e) => setAddInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCustom(addInput)}
                placeholder={editList.length >= editMax
                  ? `List is full (${editMax})`
                  : canEditCrew ? `Add a streamer to ${channel.label}` : 'Add streamer or twitch.tv link'}
                disabled={editList.length >= editMax || rosterStatus.kind === 'saving'}
                className="list-editor-input"
              />
              <button
                className="list-editor-add"
                onClick={() => addCustom(addInput)}
                disabled={!addInput.trim() || editList.length >= editMax || rosterStatus.kind === 'saving'}
              >
                Add
              </button>
            </div>
            {(canAddWatching || canImportFollows || (canEditCustom && customStreams.length > 0)) && (
              <div className="list-editor-quick">
                {canAddWatching && selectedStream && (
                  <button
                    className="quick-chip"
                    onClick={() => addCustom(watchingLogin)}
                    title="Add the stream you're watching"
                  >
                    + {selectedStream.user_name}
                  </button>
                )}
                {canImportFollows && (
                  <button
                    className="quick-chip"
                    onClick={importFollows}
                    title="Add every live channel you follow"
                  >
                    Import follows
                  </button>
                )}
                {canEditCustom && customStreams.length > 0 && (
                  <button
                    className={`quick-chip share-chip ${shareState.status === 'copied' ? 'done' : ''}`}
                    onClick={shareCurrentList}
                    disabled={shareState.status === 'busy'}
                    title="Get a link to this list — no account needed"
                  >
                    <IconShare size={11} />
                    {shareState.status === 'busy' ? 'Sharing…'
                      : shareState.status === 'copied' ? 'Link copied'
                      : 'Share list'}
                  </button>
                )}
              </div>
            )}
            {(shareState.status === 'copied' || shareState.status === 'ready') && (
              <div className="share-url" title={shareState.url}>
                {shareState.status === 'ready' ? 'Your link: ' : ''}
                <a href={shareState.url} onClick={e => e.preventDefault()}>
                  {shareState.url.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
            {shareState.status === 'error' && (
              <div className="share-url error">Couldn't create the link — try again in a minute.</div>
            )}
          </div>
        )}

        <div className="list-title-row">
          <h2>
            {listTitle} <span className="list-count">{filteredAndSortedStreams.length}</span>
          </h2>
          {!!liveCount && liveCount > 1 && (
            <span className="live-count-pill" title="People watching on pogchamp.tv right now">
              <span className="live-count-dot" />
              {liveCount} online
            </span>
          )}
          {onToggleSidebar && (
            <button
              className="list-collapse-btn"
              onClick={onToggleSidebar}
              title="Hide sidebar"
            >
              <IconChevronLeft size={16} />
            </button>
          )}
        </div>
        <div className="search-wrap">
          <IconSearch size={13} className="search-icon" />
          <input
            type="text"
            placeholder="Search streams"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="sort-options">
          <button
            className={`sort-btn ${sortBy === 'chat' ? 'active' : ''}`}
            onClick={() => setSortBy('chat')}
          >
            Chat
          </button>
          <button
            className={`sort-btn ${sortBy === 'viewers' ? 'active' : ''}`}
            onClick={() => setSortBy('viewers')}
          >
            Viewers
          </button>
          <button
            className={`sort-btn ${sortBy === 'heat' ? 'active' : ''}`}
            onClick={() => setSortBy('heat')}
          >
            Heat
          </button>
        </div>
      </div>
      <div className="stream-items">
        {filteredAndSortedStreams.length === 0 && filteredOffline.length === 0 ? (
          <div className="no-streams">
            {searchTerm.trim() ? (
              <p>No streams match “{searchTerm.trim()}”.</p>
            ) : viewMode === 'followed' ? (
              <p>No followed streams are live right now.</p>
            ) : viewMode === 'npxl' ? (
              <p>No NPXL streams are playing GTA V right now.</p>
            ) : canEditCrew && editList.length === 0 ? (
              <>
                <p className="empty-title">{channel.label} is empty</p>
                <p>
                  Add streamers above — the roster goes live for everyone the
                  moment you do.
                </p>
              </>
            ) : viewMode === 'custom' ? (
              canEdit ? (
                <>
                  <p className="empty-title">Build your own rotation</p>
                  <p>
                    Add streamers above and auto-switch will jump only between
                    the channels on your list — live data included, even for
                    channels far outside the top 100.
                  </p>
                </>
              ) : (
                <p>This list is empty.</p>
              )
            ) : viewMode === 'top' && gameFilter ? (
              <p>Nothing live in {gameFilter.name} right now.</p>
            ) : (
              <p>No streams found.</p>
            )}
          </div>
        ) : (
          <>
            {roster && filteredAndSortedStreams.length === 0 && !searchTerm.trim() && (
              <div className="roster-quiet">
                {isCrew
                  ? `No one from ${channel.label} is live right now.`
                  : 'None of your streamers are live right now.'}
              </div>
            )}
            {filteredAndSortedStreams.map((stream, index) => (
              <StreamRow
                key={stream.id}
                stream={stream}
                rank={index + 1}
                selected={selectedStream?.id === stream.id}
                editable={canEdit}
                peakRate={listPeakRate}
                onSelect={onStreamSelect}
                onRemove={canEdit ? removeCustom : undefined}
              />
            ))}
            {filteredOffline.length > 0 && (
              <>
                <div className="roster-offline-label">offline · {filteredOffline.length}</div>
                {filteredOffline.map(entry => (
                  <RosterOfflineRow
                    key={`off-${entry.login}`}
                    login={entry.login}
                    user={entry.user}
                    editable={canEdit}
                    onSelect={onStreamSelect}
                    onRemove={canEdit ? removeCustom : undefined}
                  />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
